<template>
  <div class="checkout-payment">
    <div v-if="order.business && order.trial" class="trial-message">
      <p>
        $0 due until {{ order.nextBilling | formatDate }}. Recurring billing will start only after your 14-day free trial ends.
        You can cancel anytime from your Account Settings.
      </p>

      <p>
        Tiered pricing will provide you with automated discount for the higher number of subscriptions you add. The price of each subscription is assessed within its tier. For example, buying 3 subscriptions will cost $1009.20 (2*$348 + 1*$313.20).
      </p>

      <p>
        Tier 1: 1-2 Subscriptions: (<strong>$348.00</strong> each) <br>
        Tier 2: 3-5 Subscriptions: (<strong>$313.20</strong> each) <br>
        Tier 3: 6-10 Subscriptions: (<strong>$278.40</strong> each) <br>
        Tier 4: 11-20 Subscriptions: (<strong>$243.60</strong> each) <br>
        Tier 5: 21-30 Subscriptions: (<strong>$226.20</strong> each) <br>
      </p>
    </div>
    <p class="select">
      Select a payment method
    </p>
    <div class="payment-tabs" :class="{ loading }">
      <div
        class="payment-tab" :class="{ active: paymentInfo.method === 'card' }" :data-event="`${$route.query.plan}_creditcard.click`"
        data-direct-send
        @click="setPaymentMethod('card')"
      >
        <div class="tab">
          <div class="payment-info">
            <div class="payment-name">
              Credit or Debit card
            </div>
          </div>
          <div v-if="paymentInfo.method === 'card'" class="icon-check">
            <IconCheck />
          </div>
          <div v-else class="circle" />
        </div>
      </div>
      <div
        class="payment-tab" :class="{ active: paymentInfo.method === 'pay_pal', disabled: currency.value === 'INR' }" :data-event="`${$route.query.plan}_paypal.click`"
        data-direct-send
        @click="setPaymentMethod('pay_pal')"
      >
        <div class="tab">
          <div class="payment-info">
            <div class="payment-name">
              <IconPaypalLogo /> PayPal
            </div>
          </div>
          <div v-if="paymentInfo.method === 'pay_pal'" class="icon-check">
            <IconCheck />
          </div>
          <div v-else class="circle" />
        </div>
        <div v-if="currency.value === 'INR'" class="tooltip">
          This payment method is not supported for the selected currency. To pay via PayPal, please choose another currency.
        </div>
      </div>
    </div>
    <div class="form-wrapper">
      <div v-if="paymentInfo.method === 'card'" class="payment-card">
        <form
          id="payment-form-card"
          class="payment-form"
          autocomplete="chrome-off"
        >
          <div class="host-field">
            <input
              type="hidden"
              class="host-input"
              data-chargify="firstName"
              :value="paymentInfo.card.firstName"
            >
          </div>
          <div class="host-field">
            <input
              type="hidden"
              class="host-input"
              data-chargify="lastName"
              :value="paymentInfo.card.lastName"
            >
          </div>
          <div class="host-field">
            <input
              type="hidden"
              class="host-input"
              data-chargify="state"
              :value="paymentInfo.card.state || 'unknown state'"
            >
          </div>
          <div class="host-field">
            <input
              type="hidden"
              class="host-input"
              data-chargify="address"
              :value="paymentInfo.card.address || paymentInfo.card.country"
            >
          </div>
          <div class="host-field">
            <input
              type="hidden"
              class="host-input"
              data-chargify="city"
              :value="paymentInfo.card.city || 'unknown city'"
            >
          </div>
          <div class="host-field">
            <input
              type="hidden"
              class="host-input"
              data-chargify="country"
              :value="paymentInfo.card.country"
            >
          </div>
          <div class="host-field">
            <input
              type="hidden"
              class="host-input"
              data-chargify="zip"
              :value="paymentInfo.card.zip"
            >
          </div>
          <div class="input-group card-number" :class="{ invalid: errors(['card', 'number']).any }">
            <label class="required">Card Number</label>
            <div
              class="card-number-wrapper"
              :class="{ invalid: errors(['card', 'number']).any }"
            >
              <div class="icon-card">
                <IconCreditCardCheckout />
              </div>
              <div v-if="paymentInfo.card.type" class="icon-card-type">
                <Component :is="cardIcon" />
              </div>
              <IconLock v-else class="icon-lock" />
            </div>
            <small v-if="errors(['card', 'number']).message" class="alert">{{ errors(['card', 'number']).message }}</small>
          </div>
          <div class="input-group card-name">
            <label for="name" class="required">Name on card</label>
            <div class="card-name-input-holder">
              <IconPerson class="icon-person" />
              <input
                id="name"
                v-model="nameOnCard"
                type="text"
                name="name"
                placeholder="Name On Card"
                :class="{ invalid: errors(['firstName', 'lastName']).any }"
                :data-event="`${$route.query.plan}_nameoncard.click`"
                data-direct-send
              >
            </div>
            <div class="hidden card-firstname" />
            <div class="hidden card-lastname" />
          </div>
          <div class="input-group card-expiration">
            <label class="required">Expires on</label>
            <div
              class="card-expiration-wrapper"
              :class="{ invalid: errors(['year', 'month']).any }"
            >
              <div class="card-expiration-month" />
              <div class="card-expiration-year" />
              <div class="card-expiration-slash">
                /
              </div>
            </div>
          </div>
          <div class="input-group cvv">
            <label for="cvv" class="required">CVV/CVC</label>
            <div class="tooltip-holder cvv">
              <IconQuestion class="icon-question" />
              <div class="tooltip">
                A 3-digit security code is usually found on the back of your card. American Express cards have a 4-digit code located on the front.
              </div>
            </div>
            <div
              class="card-cvv-wrapper"
              :class="{ invalid: errors(['cvv', 'security code']).any }"
            />
            <small v-if="errors(['cvv', 'security code']).message" class="alert">{{ errors(['cvv', 'security code']).message }}</small>
          </div>
          <div v-if="invoice" class="invoice-info">
            <h2 class="h6">
              Company Billing Address
            </h2>
            <div class="input-group address">
              <label for="address" class="required">Address</label>
              <input
                id="address"
                v-model="address"
                type="text"
                name="address"
                :class="{ invalid: errors('address').any }"
                :disabled="paymentProfile || loading || loadingChargify"
                placeholder="Company Address"
              >
              <div class="hidden invoice-address" />
            </div>
            <div class="input-group city">
              <label for="city" class="required">City</label>
              <input
                id="city"
                v-model="city"
                type="text"
                name="city"
                :class="{ invalid: errors('city').any }"
                :disabled="paymentProfile || loading || loadingChargify"
                placeholder="Select City"
              >
              <div class="hidden invoice-city" />
            </div>
            <div class="input-group zip-code-invoice">
              <label for="zip-code" class="required">Zip Code</label>
              <input
                id="zip-code"
                v-model="zip"
                type="text"
                name="zip-code"
                :class="{ invalid: errors('zip').any }"
                :disabled="loading || loadingChargify || paymentProfile"
                placeholder="Zip Code"
              >
              <div class="hidden card-zip" />
            </div>
          </div>
          <div class="country">
            <IconArrow />
            <label class="required">Country</label>
            <select
              v-model="country" class="form-control"
              :class="{ invalid: errors('country').any }"
              :disabled="loading || loadingChargify || paymentProfile || order.countryDisabled"
              :data-event="`${$route.query.plan}_country.click`"
              data-direct-send
            >
              <option disabled hidden value="">
                Select Country
              </option>
              <option
                v-for="(c, id) in countries"
                :key="id"
                :value="c.alpha2"
              >
                {{ c.country }}
              </option>
            </select>
            <div class="hidden card-country" />
          </div>
          <div v-if="countriesStates[country]" class="state">
            <IconArrow />
            <label class="required">State</label>
            <select
              v-model="state" class="form-control"
              :class="{ invalid: errors('state').any }"
              :disabled="loading || loadingChargify || paymentProfile"
            >
              <option disabled hidden value="">
                Select State
              </option>
              <option
                v-for="(st, id) in countriesStates[country]"
                :key="id"
                :value="st.abbreviation"
              >
                {{ st.name }}
              </option>
            </select>
          </div>
          <div v-if="!invoice" :class="{ hidden: !country, disabled: paymentProfile }" class="input-group zip-code">
            <label for="zip-code" class="required">Zip Code</label>
            <input
              id="zip-code"
              v-model="zip"
              type="text"
              placeholder="Zip Code"
              :class="{ invalid: country && errors('zip').any }"
              :disabled="loading || loadingChargify || paymentProfile"
              data-direct-send
              :data-event="`${$route.query.plan}_zip.click`"
              name="zip-code"
            >
            <div class="hidden card-zip" />
          </div>
        </form>
      </div>
      <div v-else class="payment-paypal">
        <p>
          The safer, easier way to pay, Pay Pal uses data encryption and anti-fraud technology to help keep your information secure.
        </p>
        <form
          id="payment-form-pay_pal"
          class="payment-form"
          autocomplete="chrome-off"
        >
          <div class="pay-pal-wrapper" />
          <div class="host-field">
            <input
              type="hidden"
              class="host-input"
              data-chargify="state"
              :value="paymentInfo.card.state || 'unknown state'"
            >
          </div>
          <div class="host-field">
            <input
              type="hidden"
              class="host-input"
              data-chargify="address"
              :value="paymentInfo.card.address || paymentInfo.card.country"
            >
          </div>
          <div class="host-field">
            <input
              type="hidden"
              class="host-input"
              data-chargify="city"
              :value="paymentInfo.card.city || 'unknown city'"
            >
          </div>
          <div class="host-field">
            <input
              type="hidden"
              class="host-input"
              data-chargify="country"
              :value="paymentInfo.card.country"
            >
          </div>
          <div class="host-field">
            <input
              type="hidden"
              class="host-input"
              data-chargify="zip"
              :value="paymentInfo.card.zip"
            >
          </div>
          <div v-show="invoice" class="invoice-info">
            <h2 class="h6">
              Company Billing Address
            </h2>
            <div class="input-group address">
              <label for="address" class="required">Address</label>
              <input
                id="address"
                v-model="address"
                type="text"
                name="address"
                :class="{ invalid: errors('address').any }"
                :disabled="paymentProfile || loading || loadingChargify"
                placeholder="Company Address"
              >
              <div class="hidden invoice-address" />
            </div>
            <div class="input-group city">
              <label for="city" class="required">City</label>
              <input
                id="city"
                v-model="city"
                type="text"
                name="city"
                :class="{ invalid: errors('city').any }"
                :disabled="paymentProfile || loading || loadingChargify"
                placeholder="Select City"
              >
              <div class="hidden invoice-city" />
            </div>
            <div class="input-group zip-code-invoice">
              <label for="zip-code" class="required">Zip Code</label>
              <input
                id="zip-code"
                v-model="zip"
                type="text"
                name="zip-code"
                :class="{ invalid: errors('zip').any }"
                :disabled="loading || loadingChargify || paymentProfile"
                placeholder="Zip Code"
              >
              <div class="hidden card-zip" />
            </div>
          </div>
          <div class="country">
            <IconArrow />
            <label class="required">Country</label>
            <select
              v-model="country" class="form-control"
              :class="{ invalid: errors('country').any }"
              :disabled="loading || loadingChargify || paymentProfile || order.countryDisabled"
            >
              <option disabled hidden value="">
                Select Country
              </option>
              <option
                v-for="(c, id) in countries"
                :key="id"
                :value="c.alpha2"
              >
                {{ c.country }}
              </option>
            </select>
            <div class="hidden card-country" />
          </div>
          <div v-if="countriesStates[country]" class="state">
            <IconArrow />
            <label class="required">State</label>
            <select
              v-model="state" class="form-control"
              :class="{ invalid: errors('state').any }"
              :disabled="loading || loadingChargify || paymentProfile"
            >
              <option disabled hidden value="">
                Select State
              </option>
              <option
                v-for="(st, id) in countriesStates[country]"
                :key="id"
                :value="st.abbreviation"
              >
                {{ st.name }}
              </option>
            </select>
          </div>
          <div v-if="country && !invoice" class="input-group zip-code">
            <label for="zip-code" class="required">Zip Code</label>
            <input
              id="zip-code"
              v-model="zip"
              type="text"
              name="zip-code"
              :class="{ invalid: errors('zip').any }"
              :disabled="loading || loadingChargify || paymentProfile"
              placeholder=""
            >
            <div class="hidden card-zip" />
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { COUNTRIES } from '@itbpbg/lms-components/src/utils/constants';
import { mapState, mapGetters } from 'vuex';
import IconCheck from '../../assets/svg/icon-check.svg?vue-component';
import IconPaypalLogo from '../../assets/svg/icon-paypal-logo.svg?vue-component';
import IconCreditCardCheckout from '../../assets/svg/icon-credit-card-checkout.svg?vue-component';
import IconLock from '../../assets/svg/icon-lock.svg?vue-component';
import IconPerson from '../../assets/svg/icon-person.svg?vue-component';
import IconQuestion from '../../assets/svg/icon-question.svg?vue-component';
import IconArrow from '../../assets/svg/icon-arrow.svg?vue-component';

export default {
  components: {
    IconCheck,
    IconPaypalLogo,
    IconCreditCardCheckout,
    IconLock,
    IconPerson,
    IconQuestion,
    IconArrow,
  },
  filters: {
    formatDate(v) {
      return new Date(v).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    },
  },
  data: () => ({
    stepThree: false,
  }),
  computed: {
    ...mapState('checkout', ['chargify', 'paymentInfo', 'loadingChargify', 'loading', 'order', 'currentTab', 'countriesStates']),
    ...mapGetters('checkout', ['errors', 'currency']),
    countries() {
      return COUNTRIES;
    },
    cardIcon() {
      if (['visa', 'mastercard', 'discover', 'american-express'].includes(this.paymentInfo.card.type)) {
        return require(`../../assets/svg/icon-payment-card-${this.paymentInfo.card.type}.svg?vue-component`);
      }

      return false;
    },
    nameOnCard: {
      get() {
        return this.paymentInfo.card.name;
      },
      set(value) {
        if (!this.stepThree) {
          this.$store.dispatch('dataLayer/addEvent', { event: 'checkoutStep', value: 3 });
          this.stepThree = true;
        }

        this.$store.commit('checkout/setCardName', value);
      },
    },
    country: {
      get() {
        return this.paymentInfo.card.country;
      },
      set(value) {
        if (!this.stepThree) {
          this.$store.dispatch('dataLayer/addEvent', { event: 'checkoutStep', value: 3 });
          this.stepThree = true;
        }

        this.$store.commit('checkout/setCountry', value);
      },
    },
    state: {
      get() {
        return this.paymentInfo.card.state;
      },
      set(value) {
        if (!this.stepThree) {
          this.$store.dispatch('dataLayer/addEvent', { event: 'checkoutStep', value: 3 });
          this.stepThree = true;
        }

        this.$store.commit('checkout/setState', value);
      },
    },
    zip: {
      get() {
        return this.paymentInfo.card.zip;
      },
      set(value) {
        if (!this.stepThree) {
          this.$store.dispatch('dataLayer/addEvent', { event: 'checkoutStep', value: 3 });
          this.stepThree = true;
        }

        this.$store.commit('checkout/setZip', value);
      },
    },
    invoice: {
      get() {
        return this.paymentInfo.invoice || this.order.business;
      },
    },
    address: {
      get() {
        return this.paymentInfo.card.address;
      },
      set(value) {
        this.$store.commit('checkout/setAddress', value);
      },
    },
    city: {
      get() {
        return this.paymentInfo.card.city;
      },
      set(value) {
        this.$store.commit('checkout/setCity', value);
      },
    },
    paymentProfile() {
      return this.order.paymentInfo ? this.order.paymentInfo.paymentProfileId : false;
    },
    cardPaymentChargifyConfig() {
      return {
        type: 'card',
        fields: {
          number: {
            selector: '.card-number-wrapper',
            label: ' ',
            required: true,
            message: ' ',
            style: {
              input: {
                backgroundColor: this.$config.platform === 'ds' ? '#4B5461' : '#283454',
                paddingTop: '6px',
                paddingBottom: '6px',
                paddingLeft: '52px',
                paddingRight: '75px',
                border: this.$config.platform === 'ds' ? '1px solid #565E6A' : '1px solid #4D5469',
                fontSize: '18px',
                color: '#FFF',
                placeholder: {
                  color: '#ABAFB5',
                },
              },
              label: {
                fontSize: '14px',
                color: '#FFF',
              },
              message: {
                fontSize: '14px',
                color: '#E04A4A',
              },
            },
          },
          firstName: {
            selector: '.card-firstname',
            required: true,
          },
          lastName: {
            selector: '.card-lastname',
            required: true,
          },
          month: {
            selector: '.card-expiration-month',
            label: ' ',
            required: true,
            next_focus: 'year',
            message: ' ',
            style: {
              input: {
                backgroundColor: this.$config.platform === 'ds' ? '#4B5461' : '#283454',
                paddingTop: '6px',
                paddingBottom: '6px',
                paddingLeft: '16px',
                border: this.$config.platform === 'ds' ? 'none' : '1px solid #4D5469',
                ...(this.$config.platform === 'fa' && { height: '51px' }),
                ...(this.$config.platform === 'fa' && { borderRight: 'none' }),
                borderRadius: '5px',
                borderTopRightRadius: '0px',
                borderBottomRightRadius: '0px',
                fontSize: '18px',
                color: '#FFF',
                placeholder: {
                  color: '#ABAFB5',
                },
              },
              label: {
                fontSize: '14px',
                color: '#FFF',
              },
              message: {
                fontSize: '14px',
                color: '#E04A4A',
              },
            },
          },
          year: {
            label: ' ',
            required: true,
            message: ' ',
            selector: '.card-expiration-year',
            style: {
              input: {
                backgroundColor: this.$config.platform === 'ds' ? '#4B5461' : '#283454',
                paddingTop: '6px',
                paddingBottom: '6px',
                paddingLeft: '0px',
                border: this.$config.platform === 'ds' ? 'none' : '1px solid #4D5469',
                ...(this.$config.platform === 'fa' && { height: '51px' }),
                ...(this.$config.platform === 'fa' && { borderLeft: 'none' }),
                borderRadius: '5px',
                borderTopLeftRadius: '0px',
                borderBottomLeftRadius: '0px',
                fontSize: '18px',
                color: '#FFF',
                placeholder: {
                  color: '#ABAFB5',
                },
              },
              label: {
                fontSize: '14px',
                color: '#FFF',
              },
              message: {
                fontSize: '14px',
                color: '#E04A4A',
              },
            },
          },
          cvv: {
            selector: '.card-cvv-wrapper',
            label: ' ',
            required: true,
            message: ' ',
            style: {
              input: {
                backgroundColor: this.$config.platform === 'ds' ? '#4B5461' : '#283454',
                paddingTop: '6px',
                paddingBottom: '6px',
                paddingLeft: '16px',
                border: this.$config.platform === 'ds' ? 'none' : '1px solid #4D5469',
                borderRadius: '5px',
                ...(this.$config.platform === 'ds' && { borderTopRightRadius: '0px' }),
                ...(this.$config.platform === 'ds' && { borderBottomRightRadius: '0px' }),
                fontSize: '18px',
                color: '#FFF',
                placeholder: {
                  color: '#ABAFB5',
                },
              },
              label: {
                fontSize: '14px',
                color: '#FFF',
              },
              message: {
                fontSize: '14px',
                color: '#E04A4A',
              },
            },
          },
          country: {
            selector: '.card-country',
            required: true,
          },
          zip: {
            selector: '.card-zip',
            required: true,
          },
          ...(this.invoice ? {
            address: {
              selector: '.invoice-address',
              required: true,
            },
            city: {
              selector: '.invoice-city',
              required: true,
            },
          } : {}),
        },
      };
    },
    paypalPaymentChargifyConfig() {
      return {
        type: 'pay_pal',
        selector: '.pay-pal-wrapper',
        selectorForPayPalButton: '#pay-pal',
        fields: {
          country: {
            selector: '.card-country',
            required: true,
          },
          zip: {
            selector: '.card-zip',
            required: true,
          },
          ...(this.invoice ? {
            address: {
              selector: '.invoice-address',
              required: true,
            },
            city: {
              selector: '.invoice-city',
              required: true,
            },
          } : {}),
        },
      };
    },
  },
  watch: {
    currentTab(v) {
      if (v === 'payment') {
        if (this.chargify) {
          this.chargify.unload();
        }
        setTimeout(() => {
          this.loadChargifyForm();
        }, 1000);
      }
    },
    currency() {
      if (this.paymentInfo.method === 'pay_pal') {
        this.loadChargifyForm();
      }
    },
  },
  created() {
    if (process.client) {
      if (this.currentTab === 'payment') {
        this.loadChargifyForm();
      }
    }
  },
  mounted() {
    const sendCartData = () => {
      navigator.sendBeacon(`${window.$nuxt.$config.backendUrl}/payments/checkout/cart`, new Blob([JSON.stringify({
        plan: this.paymentInfo.plan.name,
        type: this.order.upgrade ? 'upgrade' : 'new-subscription',
      })], { type: 'application/json' }));
    };

    document.addEventListener(this.$getBrowserVisibilityProp(), () => {
      const hiddenProp = this.$getBrowserDocumentHiddenProp();

      if (hiddenProp) {
        if (document.visibilityState === this.$getBrowserDocumentHiddenProp()) {
          sendCartData();
        }
      }
    });

    if (!this.$getBrowserVisibilityProp()) {
      window.addEventListener('pagehide', () => {
        sendCartData();
      });
    }
  },
  methods: {
    setPaymentMethod(method) {
      if (method === 'pay_pal' && this.currency.value === 'INR') {
        return;
      }

      this.$store.commit('checkout/setPaymentMethod', method);
      this.$nextTick(() => this.loadChargifyForm());
    },
    async loadChargifyForm() {
      if (this.paymentInfo.method === 'pay_pal' && this.currency.value === 'INR') {
        return;
      }

      this.$pollValue(() => typeof Chargify !== 'undefined');
      await this.$pollValue(() => this.paymentInfo.securityToken);

      if (this.chargify) {
        this.chargify.unload();
      }

      this.$store.commit('checkout/setLoadingChargify', true);

      this.$store.commit('checkout/initChargify');
      const config = this.paymentInfo.method === 'card' ? this.cardPaymentChargifyConfig : this.paypalPaymentChargifyConfig;

      if (this.paymentInfo.method === 'pay_pal') {
        let currency = '';

        if (this.order.paymentInfo.paymentType === 'pay_pal' && this.order.paymentInfo.paymentProfileId && this.currency.value !== 'USD') {
          currency = `_${this.currency.value}`;
        }

        config.gatewayHandle = `gateway_handle_braintree${currency}`;
      }

      this.chargify.load({
        publicKey: this.order.business ? this.$config.chargify.b2bPublicKey : this.$config.chargify.publicKey,
        serverHost: this.order.business ? this.$config.chargify.b2bServerHost : this.$config.chargify.serverHost,
        securityToken: this.paymentInfo.securityToken,
        ...config,
      }, {
        onCardTypeDetected: (type) => {
          this.$store.commit('checkout/setCardType', type);
          this.$store.dispatch('dataLayer/addEvent', { event: 'checkoutStep', value: 3 });
        },
        onPayPalAuthorized: () => {
          this.$store.dispatch('dataLayer/addEvent', { event: 'checkoutStep', value: 4 });
          this.$store.dispatch('checkout/getChargifyTokenAndPay');
        },
        onPayPalError: (err) => {
          const event = `${this.$route.query.plan}_completepayment.click`;

          window.$nuxt.$tracking.sendAsyncEvent(event, { status: 'fail', error: err });
        },
      }).then(() => this.$store.commit('checkout/setLoadingChargify', false));
    },
  },
};
</script>

<style lang="scss">
.payment {
  .trial-message {
    margin-bottom: 44px;
  }

  > p {
    color: var(--main-light);
    margin: var(--spacing-36) 0 0 0;
    font-size: 14px;
  }

  .payment-tabs {
    display: flex;
    margin-top: 10px;
    margin-bottom: var(--spacing-36);

    &.loading {
      .payment-tab {
        .tab {
          opacity: 0.3;
          pointer-events: none;
        }
      }
    }

    .payment-tab {
      width: 100%;
      max-width: 231px;
      position: relative;

      &:first-of-type {
        margin-right: 10px;
      }

      &.active {
        .tab {
          border-color: var(--accent);

          .payment-info .payment-name {
            color: var(--main-light);
          }
        }
      }

      &.disabled {
        .tab {
          opacity: 0.3;
          cursor: auto;
        }

        &:hover {
          .tooltip {
            display: block;
          }
        }
      }

      .tooltip {
        right: 50%;
        bottom: 60px;
      }

      .tab {
        height: 100%;
        display: flex;
        align-items: center;
        padding: var(--spacing-24) var(--spacing-16);
        background: var(--main-dark-shade-2);
        border: 1px solid #656C78;
        border-radius: 7px;
        cursor: pointer;

        .payment-info {
          .payment-name {
            display: flex;
            justify-content: center;
            align-items: center;
            font-weight: 500;

            svg {
              margin-right: var(--spacing-12);
            }
          }
        }

        .circle {
          width: 16px;
          height: 16px;
          border: 1px solid var(--main-dark-shade-4);
          border-radius: 50%;
          margin-left: auto;
        }

        .icon-check {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background-color: var(--accent);
          margin-left: auto;

          svg {
            width: 10px;

            path[fill] {
              fill: var(--main-light);
            }
          }
        }
      }
    }
  }

  .tooltip {
    display: none;
    position: absolute;
    right: 12px;
    bottom: 30px;
    width: 227px;
    padding: var(--spacing-16);
    background: var(--main-light);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    border-radius: 3px;
    color: var(--main-dark-shade-0);
    text-align: center;
    transform: translateX(50%);
    font-size: 14px;
    z-index: 1;

    &:after {
      content: "";
      position: absolute;
      bottom: -5px;
      left: 50%;
      margin-left: -5px;
      height: 15px;
      width: 15px;
      background-color: var(--main-light);
      clip-path: polygon(0% 0%, 100% 100%, 0% 100%);
      transform: rotate(-45deg);
      border-radius: 0 0 0 0.25em;
    }
  }

  .pay-pal-wrapper {
    display: none;
  }

  .form-wrapper {
    position: relative;
  }

  .payment-card,
  .payment-paypal {
    .payment-form {
      display: flex;
      flex-wrap: wrap;

      .hidden {
        display: none;
      }

      .invalid {
        border: 1px solid var(--error);
        overflow: hidden;
        border-radius: 5px;
      }

      .input-group {
        width: 100%;
        position: relative;
        margin-bottom: var(--spacing-24);

        &.invalid {
          border: none;
          overflow: unset;
          margin-bottom: var(--spacing-48);
        }

        &.card-number {
          .card-number-wrapper {
            height: 52px;
            position: relative;

            iframe {
              width: 100% !important;
              height: 75px !important;
              margin-top: -10px;
            }
          }

          .icon-card {
            position: absolute;
            top: 1px;
            left: 1px;
            background: #4B5461;
            width: 52px;
            bottom: 0;

            svg {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
            }
          }

          .icon-card-type {
            position: absolute;
            top: 50%;
            right: 16px;
            transform: translateY(-50%);
            display: flex;
          }

          .icon-lock {
            position: absolute;
            top: 50%;
            right: 16px;
            transform: translateY(-50%);
          }

          input {
            padding-left: 52px;
            padding-right: 52px;
          }
        }

        &.cvv {
          width: 50%;
          padding-left: 15px;

          .card-cvv-wrapper {
            height: 52px;
            position: relative;

            iframe {
              width: 100% !important;
              height: 75px !important;
              margin-top: -10px;
            }
          }

          input {
            padding-left: 52px;
            padding-right: 52px;
          }
        }

        &.card-name {
          .card-name-input-holder {
            position: relative;

            .icon-person {
              position: absolute;
              top: 50%;
              left: 16px;
              margin-top: -10px;
            }

            input {
              padding-left: 52px;
            }
          }
        }

        &.card-expiration {
          width: 50%;
          padding-right: 15px;

          .card-expiration-wrapper {
            overflow: hidden;
            position: relative;
            height: 52px;
            display: flex;

            .card-expiration-month {
              width: 62px;
            }

            .card-expiration-year {
              width: calc(100% - 62px);
            }

            iframe {
              width: 100% !important;
              height: 75px !important;
              margin-top: -10px;
            }

            .card-expiration-slash {
              position: absolute;
              left: 49px;
              top: 50%;
              transform: translateY(-50%);
              font-size: 18px;
              margin-top: -1px;
            }
          }
        }

        &.city {
          padding-right: 15px;
        }

        &.zip-code-invoice {
          padding-left: 15px;
          padding-right: 0 !important;
        }

        &.zip-code,
        &.city,
        &.zip-code-invoice {
          width: 50%;
        }

        &:nth-of-type(3) {
          width: 50%;
          padding-right: 15px;
        }

        &:nth-of-type(4) {
          width: 50%;
          padding-left: 15px;
        }

        &.zip-code {
          padding-right: 15px;
        }
      }

      .country {
        margin-top: var(--spacing-24);
      }

      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus,
      input:-webkit-autofill:active,
      select:-webkit-autofill,
      select:-webkit-autofill:hover,
      select:-webkit-autofill:focus,
      select:-webkit-autofill:active {
        transition: 0s;
        -webkit-box-shadow: 0 0 0 50px #4B5461 inset !important;
        -webkit-text-fill-color: var(--main-light) !important;
      }

      input,
      select {
        transition: 0s;
      }
    }
  }

  .invoice-info {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    margin-top: var(--spacing-48);

    .h6 {
      color: var(--main-light);
      margin-bottom: var(--spacing-24);
    }
  }

  .tooltip-holder {
    position: absolute;
    top: 50%;
    right: 16px;
    margin-top: -9px;

    &.cvv {
      z-index: 1;
      transform: translateY(50%);
    }

    .icon-question {
      width: 18px;

      &:hover + .tooltip {
        display: block;
      }
    }
  }

  input {
    background: #4B5461;
    border: 1px solid #565E6A;
    border-radius: 5px;
    color: var(--main-light);

    &::placeholder {
      color: var(--main-light-shade-4);
    }

    &:focus {
      border-color: #565E6A;
    }

    &.invalid-field {
      border-color: var(--main-red);
    }
  }

  .required {
    display: inline-block;
    padding-bottom: 5px;
    font-size: 14px;
    color: var(--main-light);

    &:after {
      padding-left: 3px;
    }
  }

  .alert {
    color: var(--main-red);
    position: absolute;
    margin-top: 6px;
  }

  .country,
  .state {
    width: 100%;
    position: relative;

    svg {
      position: absolute;
      top: 50%;
      right: 21px;
      width: 14px;
      height: 8px;
      transform: translateY(50%);
      margin-top: 5px;

      path[stroke] {
        stroke: var(--main-light-shade-4);
      }
    }

    select {
      margin-top: 5px;
      background: none;
      background-color: #4B5461;
      border: 1px solid #565E6A;
      border-radius: 5px;
      color: var(--main-light);

      option {
        color: var(--main-light-shade-4);
      }
    }
  }

  .state {
    margin-top: var(--spacing-24);
    width: 50%;
    padding-right: 15px;

    select {
      margin-top: 0;
    }

    svg {
      right: 34px;
      top: 37px;
    }

    &:not(.hidden) + .zip-code {
      padding-left: 15px;
      padding-right: 0 !important;
    }
  }

  .zip-code {
    margin-top: var(--spacing-24);
    margin-bottom: 0;
    width: 50%;
  }

  .payment-paypal {
    p {
      margin-bottom: var(--spacing-24);
      max-width: 70%;
    }
  }
}

@include bp($bp-mobile) {
  .payment {
    .payment-tabs {
      .payment-tab {
        &.disabled {
          .tooltip {
            right: 0;
            transform: translateX(0);

            &:after {
              left: unset;
              margin-left: 12px;
            }
          }
        }

        .tab {
          padding: var(--spacing-16) var(--spacing-12);

          .circle,
          .icon-check {
            flex: 0 0 auto;
          }
        }
      }
    }

    .payment-card {
      .tooltip {
        right: 98px;

        &:after {
          left: unset;
          margin-left: 12px;
        }
      }

      .payment-form {
        .country {
          margin-top: var(--spacing-36);
        }

        .input-group {
          &.card-number {
            .icon-card {
              width: 46px;
              height: 46px;
              top: 0;
            }
          }

          &.zip-code {
            width: 100%;
            padding-right: 0;
          }

          &.card-expiration {
            .card-expiration-wrapper {
              .card-expiration-slash {
                left: 50px;
              }
            }
          }
        }
      }
    }

    .payment-paypal {
      p {
        max-width: 100%;
        font-size: 14px;
      }
    }

    .zip-code {
      width: 100%;
      padding-right: 0;
    }
  }
}

.fa {
  .payment {
    .select {
      color: var(--main-light);
      font-size: 14px;
    }

    .payment-tabs {
      .payment-tab {
        .tab {
          background: var(--main-dark-light-2);
          border: 1px solid var(--main-dark-light-3);
        }

        &.active {
          .tab {
            border-color: var(--accent);
          }
        }
      }
    }

    .payment-card {
      .payment-form {
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active,
        select:-webkit-autofill,
        select:-webkit-autofill:hover,
        select:-webkit-autofill:focus,
        select:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 50px #283454 inset !important;
        }

        .input-group {
          &.card-number {
            .card-number-wrapper {
              .icon-card {
                background: var(--main-dark-light-2);
                top: 2px;
              }
            }
          }
        }
      }
    }

    input {
      background: var(--main-dark-light-2);
      border: 1px solid var(--main-dark-light-3);
    }

    .country,
    .state {
      select {
        background: var(--main-dark-light-2);
        border: 1px solid var(--main-dark-light-3);
      }
    }

    .required {
      &:after {
        color: #C75656;
      }
    }
  }

  @include bp($bp-mobile) {
    .payment {
      .payment-tabs {
        margin-bottom: var(--spacing-30);
      }

      .payment-card {
        .payment-form {
          .input-group {
            margin-bottom: var(--spacing-30);

            &.card-number {
              .card-number-wrapper {
                .icon-card {
                  height: 48px;
                }
              }
            }

            &.zip-code {
              padding-left: 0;
            }
          }

          .country {
            margin-top: 0;
          }
        }
      }

      .state {
        width: 100%;
        padding-right: 0;

        svg {
          right: 21px;
        }
      }
    }
  }
}
</style>
